import React from 'react';

import { Redirect, Route, Switch } from 'react-router-dom';

import { useMediaQuery } from '@material-ui/core';

import AppConfig from '../../../../constants/AppConfig';

import PlanningPage from '../../../../pages/planning/PlanningPage';
import AdminPage from '../../../../pages/admin';
import DiscoveryPage from '../../../../pages/discovery/DiscoveryPage';
import IFramePage from '../../../../pages/iframe/IFramePage';
import LoginPageContainer from '../../../../pages/login/LoginPageContainer';
import RegistersPage from '../../../../pages/registers/RegistersPage';
import ReportsPage from '../../../../pages/reports/ReportsPage';
import ServiceCenterPage from '../../../../pages/serviceCenter/ServiceCenterPage';
import ManagementPage from '../../../../pages/management/ManagementPage';
import CustomerServicePage from '../../../../pages/customerService/CustomerServicePage';
import SettingsPage from '../../../../pages/settings';
import AlertsPage from '../../../../pages/alerts/AlertsPage';
import TicketsPage from '../../../../pages/tickets/TicketsPage';
import AuthGuard from '../../../AuthGuard';
import ConfigPreloadGuard from '../../../ConfigPreloadGuard';
import PageTemplate from '../../../PageTemplate';
import ErrorDialog from '../../../dialogs/ErrorDialog';
import NoInternetDialog from '../../../dialogs/NoInternetDialog';
import { AccessibleRoute } from '../../../accessControl/components';
import LinkVideoPlayer from '../../../LinkVideoPlayer/LinkVideoPlayer';
import { MOBILE } from '@/app/constants/dictionaries/ScreenSizeConst';
import { ILinkVideoArgs } from '@/app/services/discovery/_shared/urlMakers';
import { useToolkitDispatch } from '@/app/hooks';
import { setVideoParams, toggleVideoDialog } from '@/app/state/ui/videoUrl';
import { paramsToObject } from '@/app/helpers/paramsToObject';
import {
    TrustedDeviceConfirmation,
    TrustedDeviceRegistration,
    TrustedDeviceResendingEmails,
} from '../../../../pages/login/TrustedDevice';
import { useFromPath } from '@/app/state/login/index.hooks';
import {
    usePrivileges,
    useRequiredActions,
} from '@/app/state/auth/index.hooks';
import chooseInitialPage from '../../../../helpers/chooseInitialPage';
import { downloadFile } from '@/app/helpers/downloadFIle';
import { DashboardExternalPage } from '@/app/pages/dashboard/DashboardExternalPage';
import { TankEditExternalPage } from '@/app/pages/registers/tanks/TankEditExternalPage';
import { VehicleEditExternalPage } from '@/app/pages/registers/vehicles/VehicleEditExternalPage';
import {
    RouteTankEditPage,
    RouteVehicleEditPage,
    RouteVehicleNewPage,
    RouteTankNewPage
} from '@/app/pages/paths';
import { NewTankExternalPage } from '@/app/pages/registers/tanks/NewTankExternalPage';
import { NewVehicleExternalPage } from '@/app/pages/registers/vehicles/NewVehicleExternalPage';

const App = () => {
    const isMobile = useMediaQuery(MOBILE);
    const toolkitDispatch = useToolkitDispatch();

    const privileges = usePrivileges();

    const redirectHandler = () => {
        if (isMobile) {
            return <Redirect to={chooseInitialPage(privileges)} />;
        }

        if (AppConfig.instance.getConfigKey(AppConfig.PROPERTY_DASHBOARD)) {
            return (
                <Redirect
                    to={
                        privileges.dashboardContext
                            ? '/dashboard'
                            : chooseInitialPage(privileges)
                    }
                />
            );
        }
        return <Redirect to={chooseInitialPage(privileges)} />;
    };

    const fromPath = useFromPath();
    const requiredActions = useRequiredActions() ?? [];

    const renderRoutes = () => {
        return fromPath?.startsWith('/user-trusted-devices') ? (
            <>
                <Switch>
                    <AccessibleRoute
                        check={(privileges) =>
                            privileges.requireDeviceConfirmation
                        }
                        path="/user-trusted-devices"
                        exact
                        component={TrustedDeviceRegistration}
                    />
                    <AccessibleRoute
                        check={(privileges) => privileges.manageTrustedDevice}
                        exact
                        path="/user-trusted-devices/confirmation"
                        component={TrustedDeviceConfirmation}
                    />
                    <AccessibleRoute
                        check={(privileges) =>
                            privileges.requireDeviceConfirmation
                        }
                        exact
                        path="/user-trusted-devices/resend"
                        component={TrustedDeviceResendingEmails}
                    />
                </Switch>
            </>
        ) : (
            <PageTemplate>
                <Switch>
                    <Route exact path="/">
                        {redirectHandler()}
                    </Route>
                    <AccessibleRoute
                        check={(privileges) => privileges.discoveryContext}
                        path="/discovery"
                        exact
                        component={DiscoveryPage}
                    />
                    {AppConfig.instance.getConfigKey(
                        AppConfig.PROPERTY_DASHBOARD
                    ) && (
                        <AccessibleRoute
                            check={(privileges) => privileges.dashboardContext}
                            path="/dashboard"
                            exact
                            component={DashboardExternalPage}
                        />
                    )}

                    {AppConfig.instance.getConfigKey(
                        AppConfig.PROPERTY_REGISTERS
                    ) && (
                        <AccessibleRoute
                            check={(privileges) => privileges.registersContext}
                            path="/registers/:tab?"
                            component={RegistersPage}
                        />
                    )}

                    <AccessibleRoute
                        check={(privileges) => privileges.newTankEditionPage}
                        path={RouteTankNewPage}
                        component={NewTankExternalPage}
                    />

                    <AccessibleRoute
                        check={(privileges) => privileges.newTankEditionPage}
                        path={RouteTankEditPage}
                        component={TankEditExternalPage}
                    />

                    <AccessibleRoute
                        check={(privileges) => privileges.registersContext}
                        path={RouteVehicleNewPage}
                        component={NewVehicleExternalPage}
                    />

                    <AccessibleRoute
                        check={(privileges) => privileges.registersContext}
                        path={RouteVehicleEditPage}
                        component={VehicleEditExternalPage}
                    />

                    {AppConfig.instance.getConfigKey(
                        AppConfig.PROPERTY_ALERTS
                    ) && (
                        <AccessibleRoute
                            check={(privileges) => privileges.alertsContext}
                            path="/alerts"
                            exact
                            component={AlertsPage}
                        />
                    )}

                    {AppConfig.instance.getConfigKey(
                        AppConfig.PROPERTY_REPORTS
                    ) && (
                        <AccessibleRoute
                            check={(privileges) => privileges.reportsContext}
                            path="/reports"
                            exact
                            component={ReportsPage}
                        />
                    )}

                    <AccessibleRoute
                        check={(privileges) =>
                            privileges.manageGroups ||
                            privileges.devicesAccess ||
                            privileges.manageProducts
                        }
                        exact
                        path="/management"
                        component={ManagementPage}
                    />
                    <AccessibleRoute
                        check={(privileges) =>
                            privileges.contextCustomerServiceCentre
                        }
                        path="/customer-service"
                        component={CustomerServicePage}
                    />
                    <AccessibleRoute
                        check={(privileges) => privileges.contextPlanning}
                        path="/planning"
                        exact
                        component={PlanningPage}
                    />
                    <AccessibleRoute
                        check={(privileges) => privileges.adminPanel}
                        path="/admin"
                        exact
                        component={AdminPage}
                    />
                    <AccessibleRoute
                        check={(privileges) => privileges.tickets}
                        path="/tickets"
                        exact
                        component={TicketsPage}
                    />

                    <Route
                        path="/user-account"
                        component={SettingsPage}
                        exact
                    />

                    <Route
                        path="/service-center"
                        component={ServiceCenterPage}
                        exact
                    />

                    <Route
                        path="/iframe/:iframe"
                        component={IFramePage}
                        exact
                    />
                    <Redirect from="*" to="/404" />
                </Switch>
                <Route
                    path="/download"
                    render={() => {
                        const urlParams = new URLSearchParams(
                            window.location.search
                        );
                        const entries = urlParams.entries();
                        const params = paramsToObject(entries) as any;

                        downloadFile(params);
                        return redirectHandler();
                    }}
                />

                <Route
                    path="/player"
                    render={() => {
                        const urlParams = new URLSearchParams(
                            window.location.search
                        );
                        const entries = urlParams.entries();
                        const params = paramsToObject(
                            entries
                        ) as ILinkVideoArgs;
                        if (params.monitoredId) {
                            toolkitDispatch(setVideoParams(params));

                            toolkitDispatch(toggleVideoDialog());
                        }
                        return redirectHandler();
                    }}
                />
                <LinkVideoPlayer />
                <NoInternetDialog />
                <ErrorDialog />
            </PageTemplate>
        );
    };
    return (
        <AuthGuard renderCover={() => <LoginPageContainer />}>
            <ConfigPreloadGuard
                fetchOnlyTranslations={requiredActions.length > 0}
            >
                {renderRoutes()}
            </ConfigPreloadGuard>
        </AuthGuard>
    );
};

export default App;
